/**
 * @file 全ページ共通のシェル (ヘッダー / メイン / フッター)。
 *
 * @what: ヘッダー左に「いちごスタディ」への導線といま選んでいるまなぶ人
 *   (LearnerSwitcher)、右にハンバーガーのメニュー (家族名・役割・家族を変える・
 *   アプリを更新する・ログアウト)。メインは通常 720px 幅、/study 配下は横幅いっぱいで、配色もスタディ面
 *   (青基調) に切り替える。
 * @why: ヘッダーがログアウトしか持たず、家族名と役割が本文に出てメインが狭かった
 *   (代表 2026-08-23)。いちごスタディの面 (4 列の見取り図・表) は幅を使うので、
 *   /study 配下は最大幅を外す。ヘッダーだけ本体 (Soft Sage) の色のままだと
 *   スタディ面から浮くので、/study 配下はシェルごと青基調にする (代表 2026-08-24)。
 *   まなぶ人の切り替えが本文のタブに埋もれていたので、Berry Buds の表記をやめ、空いた
 *   場所に出す (代表 2026-08-29)。
 * @failure: 描画のみ。家族名とまなぶ人はページが useHeaderFamily / useHeaderLearner で
 *   渡さなければ出さない。
 */

import { useCallback, useMemo, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuthState } from "../auth/AuthContext";
import { signOutCurrentUser } from "../auth/firebaseAuth";
import { AccountMenu } from "./AccountMenu";
import { HeaderContext, type HeaderFamily } from "./headerContext";
import { HeaderLearnerContext, type HeaderLearner } from "./headerLearnerContext";
import { LearnerSwitcher } from "./LearnerSwitcher";
import { useThemeMode } from "./theme";
import "./layout.css";

export function Layout({ children }: { children: ReactNode }) {
  const auth = useAuthState();
  const location = useLocation();
  const wide = location.pathname === "/study" || location.pathname.startsWith("/study/");
  const theme = useThemeMode();
  const [family, setFamilyState] = useState<HeaderFamily | null>(null);
  const setFamily = useCallback((next: HeaderFamily | null) => setFamilyState(next), []);
  const headerValue = useMemo(() => ({ family, setFamily }), [family, setFamily]);
  const [learner, setLearnerState] = useState<HeaderLearner | null>(null);
  const setLearner = useCallback((next: HeaderLearner | null) => setLearnerState(next), []);
  const learnerHeaderValue = useMemo(() => ({ learner, setLearner }), [learner, setLearner]);

  const accountLabel =
    auth.status === "authenticated"
      ? (auth.user.displayName ?? auth.user.email ?? "アカウント")
      : "";

  return (
    <HeaderContext.Provider value={headerValue}>
      <HeaderLearnerContext.Provider value={learnerHeaderValue}>
        <div className={`app-shell${wide ? " app-shell--study" : ""}`}>
          <header className="app-header">
            <div className="app-header-left">
              {auth.status === "authenticated" ? (
                <>
                  <nav className="app-nav" aria-label="主なページ">
                    <NavLink
                      to="/study"
                      className={({ isActive }) =>
                        `app-nav-link${isActive ? " app-nav-link--active" : ""}`
                      }
                    >
                      いちごスタディ
                    </NavLink>
                  </nav>
                  <LearnerSwitcher learner={learner} />
                </>
              ) : null}
            </div>
            {auth.status === "authenticated" ? (
              <AccountMenu
                family={family}
                accountLabel={accountLabel}
                onSignOut={() => {
                  void signOutCurrentUser();
                }}
                theme={wide ? theme : null}
              />
            ) : null}
          </header>
          <main className={`app-main${wide ? " app-main--wide" : ""}`}>{children}</main>
          <footer className="app-footer">© Berry Buds</footer>
        </div>
      </HeaderLearnerContext.Provider>
    </HeaderContext.Provider>
  );
}
